// PlatformLimitsPanel — UI for the /admin/platform/* settings namespace (2026-08-06).
// Every platform-wide, game-deciding limit is a self-documenting ExplainedSetting
// (3 presets + custom + plain-language consequence) per the owner directive.
//   • Daily loss cap  — GET/PUT /admin/platform/daily_loss_cap
//   • Max concurrent  — GET/PUT /admin/platform/max_concurrent_matches
// More /admin/platform/* limits can be added the same way.

import { useEffect, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import ExplainedSetting from '@baaligames/shared-ui/admin/components/ExplainedSetting';
import { PLATFORM_SETTING_SPECS, explainedPropsFromSpec } from '../../components/settingPresets';

const inr = (n: number) => `₹${Number(n).toLocaleString('en-IN')}`;
const maxConcSpec = PLATFORM_SETTING_SPECS.find((s) => s.key === 'maxConcurrentMatchesPerUser')!;

export default function PlatformLimitsPanel() {
  const [cap, setCap] = useState<number | ''>(0);
  const [maxConc, setMaxConc] = useState<number | ''>(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [d, c] = await Promise.all([
          adminApi.get<{ dailyLossCapPerUser: number }>('/admin/platform/daily_loss_cap'),
          adminApi.get<{ maxConcurrentMatchesPerUser: number }>('/admin/platform/max_concurrent_matches'),
        ]);
        if (alive) { setCap(Number(d?.dailyLossCapPerUser) || 0); setMaxConc(Number(c?.maxConcurrentMatchesPerUser) || 1); }
      } catch (e) {
        if (alive) setErr(e instanceof ApiError ? e.message : 'Could not load platform limits.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  async function save() {
    setSaving(true); setMsg(''); setErr('');
    const capVal = Math.max(0, Math.min(1_000_000, Math.floor(Number(cap) || 0)));
    const concVal = Math.max(1, Math.min(100, Math.floor(Number(maxConc) || 1)));
    try {
      const [d, c] = await Promise.all([
        adminApi.put<{ dailyLossCapPerUser: number }>('/admin/platform/daily_loss_cap', { dailyLossCapPerUser: capVal }),
        adminApi.put<{ maxConcurrentMatchesPerUser: number }>('/admin/platform/max_concurrent_matches', { maxConcurrentMatchesPerUser: concVal }),
      ]);
      setCap(Number(d?.dailyLossCapPerUser) || 0);
      setMaxConc(Number(c?.maxConcurrentMatchesPerUser) || 1);
      setMsg(`Saved — daily loss cap: ${capVal > 0 ? inr(capVal) : 'none'}; up to ${concVal} match${concVal === 1 ? '' : 'es'} at once.`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
      <header className="space-y-1">
        <h1 className="text-xl font-bold text-arena-900 dark:text-white flex items-center gap-2">
          <Icon name="shield" size={20} /> Platform limits
        </h1>
        <p className="text-[13px] text-arena-500 dark:text-neon-200/70">
          Platform-wide safety limits that apply across every game. Pick a preset — or set your own —
          and the box below always tells you exactly what it does.
        </p>
      </header>

      {loading ? (
        <div className="text-[13px] text-arena-500 dark:text-neon-200/60">Loading…</div>
      ) : (
        <>
          <ExplainedSetting
            title="Daily loss cap (per player)"
            help="Limits how much a single player can lose in a rolling 24-hour window. Once they hit the cap they can't join new matches until older losses age off. A responsible-gaming brake — note it also caps what the house earns from a heavy-losing player."
            value={cap}
            onChange={setCap}
            formatValue={(v) => (!v || Number(v) <= 0 ? 'No cap' : inr(Number(v)))}
            presets={[
              { label: 'No cap', value: 0, tagline: 'Off', tone: 'recommended',
                consequence: `Players can keep playing no matter how much they lose in a day. Maximum revenue and no daily brake — the usual choice unless you want a responsible-gaming limit.` },
              { label: 'High', value: 50000, tagline: '₹50,000', tone: 'neutral',
                consequence: `A player who has lost ₹50,000 in the last 24 hours can't join new matches until those losses age off. Only affects very heavy losers.` },
              { label: 'Tight', value: 25000, tagline: '₹25,000', tone: 'safe',
                consequence: `A player who has lost ₹25,000 in the last 24 hours is paused from new matches. A noticeable brake on heavy-loss days — and on the house's take from them.` },
            ]}
            custom={{
              kind: 'number', min: 0, max: 1_000_000, step: 1000, unitPrefix: '₹', placeholder: 'e.g. 25000',
              describe: (v) => (!v || Number(v) <= 0
                ? `No cap — players can lose any amount per day.`
                : `A player is blocked from joining new matches once their net loss in the last 24 hours passes ₹${Number(v).toLocaleString('en-IN')}. Losses "age off" after 24 hours, so the block lifts on its own. Lower = tighter brake (and less house revenue from heavy losers).`),
            }}
          />

          <ExplainedSetting {...explainedPropsFromSpec(maxConcSpec, maxConc, setMaxConc)} />

          <div className="flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving}
              className="px-5 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-sm font-bold shadow-[0_2px_10px_rgba(139,92,246,0.35)] transition active:scale-95"
            >
              {saving ? 'Saving…' : 'Save limits'}
            </button>
            {msg && <span className="text-[13px] text-emerald-600 dark:text-emerald-400">{msg}</span>}
            {err && <span className="text-[13px] text-rose-600 dark:text-rose-400">{err}</span>}
          </div>
        </>
      )}
    </div>
  );
}
